/**
 * Site Comment - lightweight review widget for Shopify preview themes.
 *
 * Drop a pin anywhere on a preview URL, leave a note, then export the whole
 * lot as Markdown or JSON that Claude Code can act on directly.
 *
 * Configuration comes from window.SiteCommentConfig (see snippets/site-comment.liquid).
 * No dependencies, no build step.
 */
(function () {
    'use strict';
  
    if (window.__siteComment) return;
  
    var CFG = Object.assign(
      {
        project: 'shopify-preview',
        template: '',
        themeId: '',
        themeName: '',
        themeRole: '',
        shop: '',
        endpoint: '',
        token: '',
        allowOnPublished: false
      },
      window.SiteCommentConfig || {}
    );
  
    var STORAGE_KEY = 'site-comment:' + CFG.project;
    var AUTHOR_KEY = 'site-comment:author';
    var PANEL_KEY = 'site-comment:panel-open';
    var SESSION_KEY = 'site-comment:session';
  
    /* ------------------------------------------------------------------ *
     * Guards
     * ------------------------------------------------------------------ */
  
    function themeRole() {
      return CFG.themeRole || shopifyTheme().role || '';
    }
  
    function shopifyTheme() {
      try {
        return (window.Shopify && window.Shopify.theme) || {};
      } catch (e) {
        return {};
      }
    }
  
    function themeAllowed() {
      if (CFG.allowOnPublished) return true;
      var role = themeRole();
      // Preview and development themes only, never the live one.
      return role !== '' && role !== 'main';
    }
  
    /**
     * A review session is started explicitly, so an ordinary preview link shows
     * nothing at all. Once started it sticks across navigation until the
     * reviewer ends it, which is what makes it usable over a whole site.
     *
     *   ?review        start (this is the link you share)
     *   ?review=off    end it
     *   Alt+Shift+C    toggle, for when the URL is inconvenient
     */
    function readSessionFlag() {
      var params = new URLSearchParams(window.location.search);
      var requested = null;
  
      if (params.has('review')) {
        requested = params.get('review') !== 'off' && params.get('review') !== '0';
      } else if (params.has('comments')) {
        // Kept for links shared before ?review existed.
        requested = params.get('comments') !== 'off';
      }
  
      if (requested !== null) {
        writeJSON(SESSION_KEY, requested);
        return requested;
      }
  
      return readJSON(SESSION_KEY, false) === true;
    }
  
    function shouldRun() {
      return themeAllowed() && readSessionFlag();
    }
  
    /* ------------------------------------------------------------------ *
     * Small helpers
     * ------------------------------------------------------------------ */
  
    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }
  
    function uid() {
      return 'c_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }
  
    function clamp(n, min, max) {
      return Math.min(max, Math.max(min, n));
    }
  
    function readJSON(key, fallback) {
      try {
        var raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) {
        return fallback;
      }
    }
  
    function writeJSON(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* private mode / quota - comments still live for this page view */
      }
    }
  
    /* ------------------------------------------------------------------ *
     * Location capture - the part that makes the export useful
     * ------------------------------------------------------------------ */
  
    var SECTION_RE = /^shopify-section-(.+)$/;
  
    function sectionInfoFor(node) {
      var info = { id: '', handle: '', type: '', blockId: '' };
      var wrapper = node.closest ? node.closest('[id^="shopify-section-"]') : null;
  
      if (wrapper) {
        var match = SECTION_RE.exec(wrapper.id);
        info.id = match ? match[1] : wrapper.id;
        // JSON template sections are named `template--<id>__<section-name>`.
        var parts = info.id.split('__');
        info.handle = parts.length > 1 ? parts[parts.length - 1].replace(/_[A-Za-z0-9]{6,}$/, '') : info.id;
        info.type =
          wrapper.getAttribute('data-section-type') ||
          (wrapper.firstElementChild && wrapper.firstElementChild.getAttribute('data-section-type')) ||
          '';
      }
  
      var block = node.closest ? node.closest('[data-block-id], [data-shopify-editor-block]') : null;
      if (block) {
        info.blockId = block.getAttribute('data-block-id') || '';
        if (!info.blockId) {
          try {
            info.blockId = JSON.parse(block.getAttribute('data-shopify-editor-block') || '{}').id || '';
          } catch (e) {
            /* attribute is not JSON - ignore */
          }
        }
      }
  
      return info;
    }
  
    function selectorFor(node) {
      if (!node || node.nodeType !== 1) return '';
      var parts = [];
      var current = node;
  
      while (current && current.nodeType === 1 && parts.length < 6) {
        if (current.id && !/^shopify-section-/.test(current.id) && document.querySelectorAll('#' + CSS.escape(current.id)).length === 1) {
          parts.unshift('#' + CSS.escape(current.id));
          break;
        }
  
        var part = current.tagName.toLowerCase();
        var classes = (current.getAttribute('class') || '')
          .split(/\s+/)
          .filter(function (c) {
            return c && !/^sc-/.test(c) && !/^(is-|js-)?active$/.test(c) && c.length < 40;
          })
          .slice(0, 2);
  
        if (classes.length) part += '.' + classes.map(CSS.escape).join('.');
  
        var parent = current.parentElement;
        if (parent) {
          var siblings = Array.prototype.filter.call(parent.children, function (child) {
            return child.tagName === current.tagName;
          });
          if (siblings.length > 1) part += ':nth-of-type(' + (siblings.indexOf(current) + 1) + ')';
        }
  
        parts.unshift(part);
        if (current === document.body) break;
        current = parent;
      }
  
      return parts.join(' > ');
    }
  
    function viewportLabel(width) {
      if (width < 480) return 'mobile';
      if (width < 768) return 'large mobile';
      if (width < 1024) return 'tablet';
      if (width < 1440) return 'laptop';
      return 'desktop';
    }
  
    /**
     * A handful of computed styles, so "this is too big" exports as
     * "currently 44px" and the change request is actionable without guesswork.
     * Values at their uninteresting default are dropped to keep the export tight.
     */
    var STYLE_PROPS = [
      { prop: 'font-size', label: 'font-size' },
      { prop: 'line-height', label: 'line-height', skip: ['normal'] },
      { prop: 'font-weight', label: 'weight', skip: ['400', 'normal'] },
      { prop: 'font-family', label: 'font', transform: firstFontFamily },
      { prop: 'letter-spacing', label: 'letter-spacing', skip: ['normal', '0px'] },
      { prop: 'text-transform', label: 'text-transform', skip: ['none'] },
      { prop: 'text-align', label: 'align', skip: ['start', 'left'] },
      { prop: 'color', label: 'colour', transform: toHex },
      { prop: 'background-color', label: 'background', transform: toHex, skip: ['transparent'] },
      { prop: 'padding', label: 'padding', skip: ['0px'] },
      { prop: 'margin', label: 'margin', skip: ['0px'] },
      { prop: 'border-radius', label: 'radius', skip: ['0px'] },
      { prop: 'display', label: 'display', skip: ['inline', 'block'] }
    ];
  
    function firstFontFamily(value) {
      return (value || '').split(',')[0].replace(/["']/g, '').trim();
    }
  
    function toHex(value) {
      var match = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/.exec(value || '');
      if (!match) return value;
      if (match[4] !== undefined && Number(match[4]) === 0) return 'transparent';
      var hex = [match[1], match[2], match[3]]
        .map(function (part) {
          return ('0' + Number(part).toString(16)).slice(-2);
        })
        .join('');
      var alpha = match[4] !== undefined && Number(match[4]) < 1 ? ' @ ' + Math.round(Number(match[4]) * 100) + '%' : '';
      return '#' + hex + alpha;
    }
  
    function shorthand(computed, base) {
      var sides = ['top', 'right', 'bottom', 'left'].map(function (side) {
        return computed.getPropertyValue(base + '-' + side);
      });
      if (sides.every(function (v) { return v === sides[0]; })) return sides[0];
      if (sides[0] === sides[2] && sides[1] === sides[3]) return sides[0] + ' ' + sides[1];
      return sides.join(' ');
    }
  
    function captureStyles(node) {
      var computed = window.getComputedStyle(node);
      var styles = {};
  
      STYLE_PROPS.forEach(function (spec) {
        var raw = spec.prop === 'padding' || spec.prop === 'margin'
          ? shorthand(computed, spec.prop)
          : computed.getPropertyValue(spec.prop);
        var value = spec.transform ? spec.transform(raw) : (raw || '').trim();
        if (!value) return;
        if (spec.skip && spec.skip.indexOf(value) !== -1) return;
        if (spec.prop === 'border-radius' && /^0px/.test(value)) return;
        styles[spec.label] = value;
      });
  
      return styles;
    }
  
    function captureLocation(node, clientX, clientY) {
      var rect = node.getBoundingClientRect();
      var section = sectionInfoFor(node);
      var text = (node.textContent || '').replace(/\s+/g, ' ').trim();
  
      return {
        selector: selectorFor(node),
        tag: node.tagName.toLowerCase(),
        classes: (node.getAttribute('class') || '').split(/\s+/).filter(Boolean).slice(0, 4).join(' '),
        elementId: node.id || '',
        sectionId: section.id,
        sectionHandle: section.handle,
        sectionType: section.type,
        blockId: section.blockId,
        textSnippet: text.slice(0, 140),
        box: { width: Math.round(rect.width), height: Math.round(rect.height) },
        styles: captureStyles(node),
        ratioX: rect.width ? clamp((clientX - rect.left) / rect.width, 0, 1) : 0.5,
        ratioY: rect.height ? clamp((clientY - rect.top) / rect.height, 0, 1) : 0.5,
        pageY: Math.round(clientY + window.scrollY)
      };
    }
  
    function capturePage() {
      return {
        url: window.location.href,
        path: window.location.pathname + window.location.search,
        title: document.title,
        template: CFG.template,
        shop: CFG.shop,
        themeId: String(CFG.themeId || shopifyTheme().id || ''),
        themeName: CFG.themeName || shopifyTheme().name || '',
        themeRole: themeRole()
      };
    }
  
    function captureViewport() {
      var width = window.innerWidth;
      return {
        width: width,
        height: window.innerHeight,
        dpr: window.devicePixelRatio || 1,
        label: viewportLabel(width)
      };
    }
  
    /* ------------------------------------------------------------------ *
     * Store
     * ------------------------------------------------------------------ */
  
    var store = {
      comments: readJSON(STORAGE_KEY, []),
  
      save: function () {
        writeJSON(STORAGE_KEY, this.comments);
      },
  
      forThisPage: function () {
        var path = window.location.pathname;
        return this.comments.filter(function (c) {
          return c.page && (c.page.path || '').split('?')[0] === path;
        });
      },
  
      add: function (comment) {
        this.comments.push(comment);
        this.save();
        sync.push(comment);
      },
  
      update: function (id, patch) {
        var found = null;
        this.comments = this.comments.map(function (c) {
          if (c.id !== id) return c;
          found = Object.assign({}, c, patch, { updatedAt: new Date().toISOString() });
          return found;
        });
        this.save();
        if (found) sync.push(found);
      },
  
      remove: function (id) {
        this.comments = this.comments.filter(function (c) {
          return c.id !== id;
        });
        this.save();
        sync.remove(id);
      },
  
      merge: function (incoming) {
        var byId = {};
        this.comments.concat(incoming || []).forEach(function (c) {
          if (!c || !c.id) return;
          var existing = byId[c.id];
          if (!existing || (c.updatedAt || c.createdAt || '') >= (existing.updatedAt || existing.createdAt || '')) {
            byId[c.id] = c;
          }
        });
        this.comments = Object.keys(byId)
          .map(function (id) {
            return byId[id];
          })
          .sort(function (a, b) {
            return (a.createdAt || '') < (b.createdAt || '') ? -1 : 1;
          });
        this.save();
      }
    };
  
    /* ------------------------------------------------------------------ *
     * Optional shared backend (see worker/)
     * ------------------------------------------------------------------ */
  
    var sync = {
      enabled: function () {
        return Boolean(CFG.endpoint);
      },
  
      headers: function () {
        var headers = { 'Content-Type': 'application/json' };
        if (CFG.token) headers['Authorization'] = 'Bearer ' + CFG.token;
        return headers;
      },
  
      pull: function () {
        if (!this.enabled()) return Promise.resolve();
        return fetch(CFG.endpoint + '?project=' + encodeURIComponent(CFG.project), {
          headers: this.headers()
        })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (data) {
            if (!data || !Array.isArray(data.comments)) return;
            store.merge(data.comments);
            ui.render();
          })
          .catch(function () {
            /* offline - local copy still works */
          });
      },
  
      push: function (comment) {
        if (!this.enabled()) return;
        fetch(CFG.endpoint, {
          method: 'POST',
          headers: this.headers(),
          body: JSON.stringify({ project: CFG.project, comment: comment })
        }).catch(function () {});
      },
  
      remove: function (id) {
        if (!this.enabled()) return;
        fetch(CFG.endpoint + '/' + encodeURIComponent(id) + '?project=' + encodeURIComponent(CFG.project), {
          method: 'DELETE',
          headers: this.headers()
        }).catch(function () {});
      }
    };
  
    /* ------------------------------------------------------------------ *
     * Export
     * ------------------------------------------------------------------ */
  
    function fileHint(comment) {
      var loc = comment.location || {};
      if (loc.sectionHandle) return 'sections/' + loc.sectionHandle + '.liquid';
      if (comment.page && comment.page.template) return 'templates/' + comment.page.template + '.json';
      return '';
    }
  
    function toMarkdown(comments) {
      var open = comments.filter(function (c) {
        return c.status !== 'resolved';
      });
  
      var lines = [];
      lines.push('# Site review comments');
      lines.push('');
      lines.push('- Project: `' + CFG.project + '`');
      if (CFG.shop) lines.push('- Shop: `' + CFG.shop + '`');
      var theme = capturePage();
      if (theme.themeName || theme.themeId) {
        lines.push('- Theme: `' + (theme.themeName || 'unnamed') + ' (' + (theme.themeId || 'unknown id') + ')`');
      }
      lines.push('- Exported: ' + new Date().toISOString());
      lines.push('- Open comments: ' + open.length + ' of ' + comments.length);
      lines.push('');
      lines.push('Each item below is a change request pinned to a specific element. Use the');
      lines.push('section handle and selector to find the code, and honour the viewport note');
      lines.push('when the request is breakpoint specific.');
      lines.push('');
  
      var pages = {};
      open.forEach(function (c) {
        var key = (c.page && c.page.path) || '/';
        (pages[key] = pages[key] || []).push(c);
      });
  
      Object.keys(pages)
        .sort()
        .forEach(function (path) {
          var group = pages[path];
          var page = group[0].page || {};
          lines.push('## ' + path);
          lines.push('');
          if (page.template) lines.push('Template: `' + page.template + '`  ');
          if (page.title) lines.push('Page title: ' + page.title + '  ');
          lines.push('URL: ' + (page.url || path));
          lines.push('');
  
          group.forEach(function (c, i) {
            var loc = c.location || {};
            var vp = c.viewport || {};
            lines.push('### ' + (i + 1) + '. ' + (c.text || '').split('\n')[0].slice(0, 80));
            lines.push('');
            lines.push('> ' + (c.text || '').replace(/\n/g, '\n> '));
            lines.push('');
            if (loc.sectionHandle) lines.push('- Section: `' + loc.sectionHandle + '`' + (loc.sectionId ? ' (id `' + loc.sectionId + '`)' : ''));
            if (loc.sectionType) lines.push('- Section type: `' + loc.sectionType + '`');
            if (loc.blockId) lines.push('- Block id: `' + loc.blockId + '`');
            var hint = fileHint(c);
            if (hint) lines.push('- Likely file: `' + hint + '`');
            if (loc.selector) lines.push('- Selector: `' + loc.selector + '`');
            if (loc.textSnippet) lines.push('- Element text: "' + loc.textSnippet + '"');
            var styles = Object.keys(loc.styles || {}).map(function (label) {
              return label + ' ' + loc.styles[label];
            });
            if (loc.box && loc.box.width) styles.unshift('box ' + loc.box.width + '×' + loc.box.height + 'px');
            if (styles.length) lines.push('- Current styles: ' + styles.join(', '));
            if (vp.width) lines.push('- Viewport: ' + vp.width + '×' + vp.height + 'px (' + vp.label + ')');
            lines.push('- Author: ' + (c.author || 'unknown') + ' — ' + (c.createdAt || ''));
            lines.push('');
          });
        });
  
      if (!open.length) {
        lines.push('_No open comments._');
        lines.push('');
      }
  
      return lines.join('\n');
    }
  
    function download(filename, content, type) {
      var blob = new Blob([content], { type: type });
      var url = URL.createObjectURL(blob);
      var link = el('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 1000);
    }
  
    function copy(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
      }
      var area = el('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
      } catch (e) {
        /* nothing else to try */
      }
      document.body.removeChild(area);
      return Promise.resolve();
    }
  
    /* ------------------------------------------------------------------ *
     * UI
     * ------------------------------------------------------------------ */
  
    var ui = {
      root: null,
      panel: null,
      listEl: null,
      launcher: null,
      pinLayer: null,
      highlight: null,
      hint: null,
      composer: null,
      picking: false,
      panelOpen: readJSON(PANEL_KEY, false),
      activeId: null,
      pins: [],
  
      mount: function () {
        this.root = el('div', 'sc-root');
        this.root.setAttribute('data-site-comment', 'root');
        document.body.appendChild(this.root);
  
        // Saving lives in the dock rather than inside the comment panel, so it
        // is a deliberate end-of-session action rather than part of commenting.
        var dock = el('div', 'sc-dock');
  
        this.saveAllBtn = el('button', 'sc-dock-btn sc-dock-btn--save', 'Save all');
        this.saveAllBtn.type = 'button';
        this.saveAllBtn.title = 'Download every comment as Markdown';
        this.saveAllBtn.addEventListener('click', function () {
          download('site-comments-' + CFG.project + '.md', toMarkdown(store.comments), 'text/markdown');
          ui.toast('Saved to your downloads');
        });
        dock.appendChild(this.saveAllBtn);
  
        this.launcher = el('button', 'sc-launcher');
        this.launcher.type = 'button';
        this.launcher.appendChild(el('span', null, 'Comments'));
        this.launcherCount = el('span', 'sc-launcher-count', '0');
        this.launcher.appendChild(this.launcherCount);
        this.launcher.addEventListener('click', function () {
          ui.togglePanel(!ui.panelOpen);
        });
        dock.appendChild(this.launcher);
  
        this.root.appendChild(dock);
  
        this.buildPanel();
        this.render();
  
        // Bound once, because a session can be started and ended repeatedly.
        if (!this.listenersBound) {
          this.listenersBound = true;
          var reposition = function () {
            if (ui.root) ui.positionPins();
          };
          window.addEventListener('scroll', reposition, { passive: true });
          window.addEventListener('resize', reposition, { passive: true });
          document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape') ui.stopPicking();
          });
        }
      },
  
      buildPanel: function () {
        var panel = el('div', 'sc-panel');
        panel.hidden = true;
  
        var head = el('div', 'sc-panel-head');
        head.appendChild(el('div', 'sc-panel-title', 'Site comments'));
  
        this.addBtn = el('button', 'sc-btn sc-btn--primary', 'Add comment');
        this.addBtn.type = 'button';
        this.addBtn.addEventListener('click', function () {
          ui.picking ? ui.stopPicking() : ui.startPicking();
        });
        head.appendChild(this.addBtn);
  
        var end = el('button', 'sc-btn sc-btn--icon', 'End');
        end.type = 'button';
        end.title = 'End the review session and hide the widget';
        end.addEventListener('click', function () {
          ui.endSession();
        });
        head.appendChild(end);
  
        var close = el('button', 'sc-btn sc-btn--icon', '✕');
        close.type = 'button';
        close.title = 'Hide panel';
        close.addEventListener('click', function () {
          ui.togglePanel(false);
        });
        head.appendChild(close);
        panel.appendChild(head);
  
        var sub = el('div', 'sc-panel-sub');
        this.scopeEl = el('span', null, '');
        sub.appendChild(this.scopeEl);
        panel.appendChild(sub);
  
        this.listEl = el('div', 'sc-panel-body');
        panel.appendChild(this.listEl);
  
        var foot = el('div', 'sc-panel-foot');
        foot.appendChild(el('span', 'sc-foot-note', 'Use Save all when you are done.'));
        var extras = el('div', 'sc-foot-extras');
        extras.appendChild(this.footLink('Copy', function () {
          copy(toMarkdown(store.comments)).then(function () {
            ui.toast('Markdown copied');
          });
        }));
        extras.appendChild(this.footLink('JSON', function () {
          download(
            'site-comments-' + CFG.project + '.json',
            JSON.stringify({ project: CFG.project, exportedAt: new Date().toISOString(), comments: store.comments }, null, 2),
            'application/json'
          );
        }));
        foot.appendChild(extras);
  
        panel.appendChild(foot);
  
        this.panel = panel;
        this.root.appendChild(panel);
        if (this.panelOpen) this.togglePanel(true);
      },
  
      footLink: function (label, onClick) {
        var button = el('button', 'sc-btn--link', label);
        button.type = 'button';
        button.addEventListener('click', onClick);
        return button;
      },
  
      togglePanel: function (open) {
        this.panelOpen = open;
        this.panel.hidden = !open;
        this.launcher.classList.toggle('sc-launcher--active', open);
        writeJSON(PANEL_KEY, open);
        if (!open) this.stopPicking();
        this.render();
      },
  
      endSession: function (silent) {
        writeJSON(SESSION_KEY, false);
        this.stopPicking();
        this.closeComposer();
        if (this.root) this.root.remove();
        this.root = null;
        this.panel = null;
        this.listEl = null;
        this.pinLayer = null;
        this.pins = [];
        this.activeId = null;
        window.__siteComment.active = false;
        if (!silent) {
          // Comments survive - only the UI goes away.
          window.console && console.info('[site-comment] Review session ended. Add ?review to the URL to start again.');
        }
      },
  
      toast: function (message) {
        var node = el('div', 'sc-toast', message);
        this.root.appendChild(node);
        setTimeout(function () {
          node.remove();
        }, 1800);
      },
  
      visibleComments: function () {
        var path = window.location.pathname;
        var here = [];
        var elsewhere = [];
        store.comments.forEach(function (comment) {
          var onThisPage = ((comment.page && comment.page.path) || '').split('?')[0] === path;
          (onThisPage ? here : elsewhere).push(comment);
        });
        return here.concat(elsewhere);
      },
  
      isOnThisPage: function (comment) {
        return ((comment.page && comment.page.path) || '').split('?')[0] === window.location.pathname;
      },
  
      render: function () {
        var comments = this.visibleComments();
        var here = store.forThisPage();
  
        this.launcherCount.textContent = String(here.length);
        if (this.scopeEl) {
          this.scopeEl.textContent =
            here.length + ' on this page · ' + store.comments.length + ' in total' + (sync.enabled() ? ' · shared' : '');
        }
        if (this.saveAllBtn) {
          this.saveAllBtn.textContent = 'Save all (' + store.comments.length + ')';
          this.saveAllBtn.disabled = store.comments.length === 0;
        }
  
        if (this.listEl) {
          this.listEl.innerHTML = '';
          if (!comments.length) {
            this.listEl.appendChild(
              el('div', 'sc-empty', 'No comments yet. Hit "Add comment", then click the part of the page you want to change.')
            );
          } else {
            var pinNumber = 0;
            comments.forEach(function (comment) {
              var onThisPage = ui.isOnThisPage(comment);
              if (onThisPage) pinNumber += 1;
              ui.listEl.appendChild(ui.renderItem(comment, onThisPage ? pinNumber : null));
            });
          }
        }
  
        this.renderPins();
      },
  
      renderItem: function (comment, index) {
        var loc = comment.location || {};
        var vp = comment.viewport || {};
        var onThisPage = this.isOnThisPage(comment);
        var item = el('div', 'sc-item');
        item.dataset.id = comment.id;
        item.dataset.offpage = String(!onThisPage);
  
        item.appendChild(el('div', 'sc-item-index', onThisPage ? String(index) : '·'));
  
        var main = el('div', 'sc-item-main');
        main.appendChild(el('div', 'sc-item-text', comment.text));
  
        var metaBits = [comment.author || 'unknown'];
        if (loc.sectionHandle) metaBits.push(loc.sectionHandle);
        if (vp.width) metaBits.push(vp.width + 'px');
        if (!onThisPage && comment.page) metaBits.push(comment.page.path);
        main.appendChild(el('div', 'sc-item-meta', metaBits.join(' · ')));
        item.appendChild(main);
  
        var remove = el('button', 'sc-btn--icon sc-item-remove', '✕');
        remove.type = 'button';
        remove.title = 'Delete comment';
        remove.addEventListener('click', function (event) {
          event.stopPropagation();
          store.remove(comment.id);
          ui.render();
        });
        item.appendChild(remove);
  
        item.addEventListener('click', function () {
          ui.focusComment(comment);
        });
        return item;
      },
  
      focusComment: function (comment) {
        if (!this.isOnThisPage(comment) && comment.page && comment.page.url) {
          window.location.href = comment.page.url;
          return;
        }
        this.activeId = comment.id;
        var node = this.resolveTarget(comment);
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.renderPins();
      },
  
      resolveTarget: function (comment) {
        var loc = comment.location || {};
        if (!loc.selector) return null;
        try {
          return document.querySelector(loc.selector);
        } catch (e) {
          return null;
        }
      },
  
      renderPins: function () {
        if (!this.pinLayer) {
          this.pinLayer = el('div', 'sc-pin-layer');
          this.root.appendChild(this.pinLayer);
        }
        this.pinLayer.innerHTML = '';
        this.pins = [];
  
        store.forThisPage().forEach(function (comment, index) {
          var node = ui.resolveTarget(comment);
          var pin = el('button', 'sc-pin', String(index + 1));
          pin.type = 'button';
          pin.dataset.status = comment.status || 'open';
          pin.dataset.active = String(ui.activeId === comment.id);
          pin.title = comment.text;
          if (!node) pin.classList.add('sc-pin--orphan');
          pin.addEventListener('click', function () {
            ui.activeId = comment.id;
            ui.togglePanel(true);
            ui.renderPins();
          });
          ui.pinLayer.appendChild(pin);
          ui.pins.push({ pin: pin, node: node, comment: comment });
        });
  
        this.positionPins();
      },
  
      positionPins: function () {
        var viewportHeight = window.innerHeight;
        this.pins.forEach(function (entry) {
          var loc = entry.comment.location || {};
          if (!entry.node) {
            entry.pin.style.left = '14px';
            entry.pin.style.top = clamp(loc.pageY - window.scrollY, 40, viewportHeight - 40) + 'px';
            return;
          }
          var rect = entry.node.getBoundingClientRect();
          entry.pin.style.left = rect.left + rect.width * (loc.ratioX == null ? 0.5 : loc.ratioX) + 'px';
          entry.pin.style.top = rect.top + rect.height * (loc.ratioY == null ? 0.5 : loc.ratioY) + 'px';
          var visible = rect.bottom > -80 && rect.top < viewportHeight + 80;
          entry.pin.style.display = visible ? '' : 'none';
        });
      },
  
      /* ---------- picking ---------- */
  
      startPicking: function () {
        this.picking = true;
        this.addBtn.classList.add('sc-btn--active');
        this.addBtn.textContent = 'Cancel';
        document.documentElement.classList.add('sc-picking');
  
        this.hint = el('div', 'sc-hint', 'Click the element you want to comment on · Esc to cancel');
        this.root.appendChild(this.hint);
        this.highlight = el('div', 'sc-highlight');
        this.root.appendChild(this.highlight);
  
        this.onMove = this.handleMove.bind(this);
        this.onClick = this.handleClick.bind(this);
        document.addEventListener('mousemove', this.onMove, true);
        document.addEventListener('click', this.onClick, true);
      },
  
      stopPicking: function () {
        if (!this.picking) return;
        this.picking = false;
        this.addBtn.classList.remove('sc-btn--active');
        this.addBtn.textContent = 'Add comment';
        document.documentElement.classList.remove('sc-picking');
        if (this.hint) this.hint.remove();
        if (this.highlight) this.highlight.remove();
        document.removeEventListener('mousemove', this.onMove, true);
        document.removeEventListener('click', this.onClick, true);
      },
  
      isOwnNode: function (node) {
        return Boolean(node && node.closest && node.closest('[data-site-comment="root"]'));
      },
  
      handleMove: function (event) {
        var node = event.target;
        if (this.isOwnNode(node) || !this.highlight) return;
        var rect = node.getBoundingClientRect();
        this.highlight.style.left = rect.left + 'px';
        this.highlight.style.top = rect.top + 'px';
        this.highlight.style.width = rect.width + 'px';
        this.highlight.style.height = rect.height + 'px';
      },
  
      handleClick: function (event) {
        var node = event.target;
        if (this.isOwnNode(node)) return;
        event.preventDefault();
        event.stopPropagation();
        var location = captureLocation(node, event.clientX, event.clientY);
        this.stopPicking();
        this.openComposer(location, event.clientX, event.clientY);
      },
  
      /* ---------- composer ---------- */
  
      openComposer: function (location, x, y) {
        this.closeComposer();
  
        var savedAuthor = readJSON(AUTHOR_KEY, '') || '';
        var box = el('div', 'sc-composer');
  
        var target = [location.tag];
        if (location.sectionHandle) target.push('section: ' + location.sectionHandle);
        if (location.textSnippet) target.push('"' + location.textSnippet.slice(0, 48) + '"');
        box.appendChild(el('div', 'sc-composer-target', target.join(' · ')));
  
        var textarea = el('textarea', 'sc-textarea');
        textarea.placeholder = 'What needs changing here?';
        box.appendChild(textarea);
  
        // The name is asked for once, then remembered, so the usual flow is
        // just type and add.
        var author = el('input', 'sc-input');
        author.type = 'text';
        author.placeholder = 'Your name';
        author.value = savedAuthor;
  
        var row = el('div', 'sc-composer-row');
        var add = el('button', 'sc-btn sc-btn--primary', 'Add');
        add.type = 'button';
  
        if (savedAuthor) {
          var who = el('div', 'sc-composer-who');
          who.appendChild(el('span', null, 'as ' + savedAuthor));
          var change = el('button', 'sc-btn--link', 'change');
          change.type = 'button';
          change.addEventListener('click', function () {
            who.remove();
            row.insertBefore(author, add);
            author.focus();
          });
          who.appendChild(change);
          row.appendChild(who);
        } else {
          row.appendChild(author);
        }
  
        add.addEventListener('click', function () {
          var text = textarea.value.trim();
          if (!text) {
            textarea.focus();
            return;
          }
          writeJSON(AUTHOR_KEY, author.value.trim());
          store.add({
            id: uid(),
            createdAt: new Date().toISOString(),
            author: author.value.trim() || 'anonymous',
            text: text,
            status: 'open',
            page: capturePage(),
            location: location,
            viewport: captureViewport()
          });
          ui.closeComposer();
          ui.togglePanel(true);
          ui.toast('Comment added');
        });
        row.appendChild(add);
        box.appendChild(row);
  
        textarea.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            add.click();
          }
          if (event.key === 'Escape') ui.closeComposer();
        });
  
        this.root.appendChild(box);
        var width = box.offsetWidth;
        var height = box.offsetHeight;
        box.style.left = clamp(x - width / 2, 8, Math.max(8, window.innerWidth - width - 8)) + 'px';
        box.style.top = clamp(y + 16, 8, Math.max(8, window.innerHeight - height - 8)) + 'px';
  
        this.composer = box;
        textarea.focus();
      },
  
      closeComposer: function () {
        if (this.composer) {
          this.composer.remove();
          this.composer = null;
        }
      }
    };
  
    /* ------------------------------------------------------------------ *
     * Boot
     * ------------------------------------------------------------------ */
  
    function startSession() {
      if (!themeAllowed()) {
        window.console && console.warn('[site-comment] Not available on the published theme.');
        return;
      }
      if (window.__siteComment.active) return;
      writeJSON(SESSION_KEY, true);
      window.__siteComment.active = true;
      ui.mount();
      sync.pull();
    }
  
    function boot() {
      window.__siteComment = {
        config: CFG,
        store: store,
        ui: ui,
        active: false,
        start: startSession,
        end: function () {
          ui.endSession();
        },
        markdown: function () {
          return toMarkdown(store.comments);
        }
      };
  
      // Alt+Shift+C toggles a session without touching the URL.
      document.addEventListener('keydown', function (event) {
        if (event.altKey && event.shiftKey && (event.key === 'C' || event.key === 'c')) {
          event.preventDefault();
          window.__siteComment.active ? ui.endSession() : startSession();
        }
      });
  
      if (shouldRun()) startSession();
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  })();